import type { MouseEvent } from 'react'

import { useTranslation } from 'react-i18next'

import { IconButton, Tooltip } from '@enechain/ecloud-designsystem'
import {
  FaCopyRegular,
  FaEyeRegular,
  FaPenToSquareRegular,
  FaTrashCanRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { SquareIconButton } from '~/components/common/SquareIconButton'
import { iconSize } from '~/constants/iconSize'

type Props = {
  /**
   * 押下時のハンドラ。 click event を受け取れる (= cmd/ctrl+クリックでの別タブ
   * 遷移を扱う導線がある。 例: ワークフロー一覧の「詳細」)。 event を使わない
   * 呼び出し側は従来どおり引数なしの関数を渡してよい。
   */
  onClick: (event: MouseEvent<HTMLButtonElement>) => void
  ariaLabel?: string
  /** true のとき disabled 表示にする (= 押下不可)。 未指定時は false。 */
  disabled?: boolean
  /**
   * disabled=true の時に tooltip (= native `title` 属性) として表示する理由文言。
   * disabled が false の場合は無視される (= `ariaLabel` が title としても使われる)。
   */
  disabledReason?: string
}

/**
 * 編集アイコンボタン。 MasterDataTable の renderRowActions 内で使う想定。
 * 設計 HTML `.btn .btn-tertiary .btn-square` (= 32x32 / 白背景 / 中立枠線 / radius 4px) を
 * [[SquareIconButton]] と ecloud-icons の pen-to-square で再現する (icon 22x22 = optical sizing)。
 */
export const EditIconButton = ({
  onClick,
  ariaLabel,
  disabled = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const label = ariaLabel ?? t('common:table.action.edit')
  return (
    // アイコンのみのボタンは hover でボタン名を出す (2026-08-17 デザイナー指摘、
    // dev 全体の共通ルール)。 native `title` 属性は表示まで約 1 秒かかり見た目も
    // OS 依存なので、 DS Tooltip で出す。 `title` は Tooltip 非対応の環境
    // (タッチ・支援技術の一部) 向けの保険として残す。
    // disabled の間も DeleteIconButton と同じく ButtonTooltip で包む (UI 辞書
    // components/action-reason.md)。 SquareIconButton は aria-disabled なのでボタン自身が focus を受ける
    <ButtonTooltip content={label}>
      <SquareIconButton
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        // disabled の要素に native title は付けない (ブラウザは出さない。 action-reason.md 区分 2)
        title={disabled ? undefined : label}
      >
        <FaPenToSquareRegular boxSize={iconSize.rowActionEdit} />
      </SquareIconButton>
    </ButtonTooltip>
  )
}

/**
 * 複製アイコンボタン。 MasterDataTable の renderRowActions 内で使う想定。
 * 見た目は EditIconButton と同じ tertiary square (32x32 / 白背景 / 中立枠線 / radius 4px) で、
 * icon だけ copy に差し替える。 一覧の操作列で編集・削除と横並びに置く
 * (他ページの操作列と同じ 32x32・variant 規約)。 icon 寸法は他アクション同様 `iconSize.rowAction` (20px)。
 */
export const DuplicateIconButton = ({
  onClick,
  ariaLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const label = ariaLabel ?? t('common:table.action.duplicate')
  return (
    // EditIconButton と同じ理由で DS Tooltip (2026-08-17 デザイナー指摘: 複製だけ
    // tooltip が出ていなかった)。
    <Tooltip content={label}>
      <SquareIconButton aria-label={label} onClick={onClick} title={label}>
        <FaCopyRegular boxSize={iconSize.rowAction} />
      </SquareIconButton>
    </Tooltip>
  )
}

/**
 * 詳細 (閲覧) アイコンボタン。 MasterDataTable の renderRowActions 内で使う想定。
 *
 * **編集導線と絵で区別する**ため eye を使う (2026-09-09 デザイナー指定:「全画面対象、
 * アイコンボタンで、編集ではなく詳細の場合は `FaEyeRegular` を適用して編集導線と
 * 区別できるように」)。 それまでは詳細導線も `EditIconButton` (pen-to-square) を
 * 流用しており、 一覧の操作列だけを見ても**その行を開くと編集できるのか閲覧だけなのかが
 * 絵から読めなかった**。
 *
 * 見た目 (32x32 / 白背景 / 中立枠線 / radius 4px) は `EditIconButton` と揃える。
 * 変えるのは icon だけ = 「同じ種類の操作で、 対象の意味が違う」 ことを絵で示す。
 *
 * icon 寸法は行アクション共通の `rowAction` (20px)。 `rowActionEdit` (22px) は
 * **pen-to-square 専用の光学補正**で (design 側も
 * `.btn-square[title*="編集"] svg { width: 22px }` と編集だけに当てている)、
 * eye に当てると同じ操作列で 1 つだけ大きく見える (2026-09-09 レビュー指摘)。
 */
export const DetailIconButton = ({
  onClick,
  ariaLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const label = ariaLabel ?? t('common:action.detail')
  return (
    // EditIconButton と同じ理由で DS Tooltip (アイコンのみのボタンは hover で
    // ボタン名を出す。 2026-08-17 デザイナー指摘、 dev 全体の共通ルール)。
    <Tooltip content={label}>
      <SquareIconButton aria-label={label} onClick={onClick} title={label}>
        <FaEyeRegular boxSize={iconSize.rowAction} />
      </SquareIconButton>
    </Tooltip>
  )
}

/**
 * 削除アイコンボタン。 MasterDataTable の renderRowActions 内で使う想定。
 * 設計 HTML `.btn .btn-outline-danger .btn-square` (= 32x32 / 白背景 / 赤枠線) を ecloud-icons の
 * trash-can で再現する。 設計実測 (border 1px / radius 4px / icon 20x20) に合わせ、
 * DS IconButton の丸み・大アイコンを上書きする (枠の幅は button recipe の outline)。 枠線色は token
 * `Border.negativeMid` (= #d80004)、 アイコン色も同値の `Text.negativeMid` を明示する
 * (= colorPalette 既定の暗い赤 #ac0004 ではなく設計の #d80004 に揃える)。
 * outline の地は透明で表の縞・行の hover が透けるので、 地は `Surface.primary` を明示する
 * (UI 辞書 components/table.md §1「実装時の注意」)。 地を style prop で当てると recipe の hover の地より
 * 強くなるので、 hover の地 (design `.btn-outline-danger:hover` = `Hover.negativeLow`) も併記する。
 */
export const DeleteIconButton = ({
  onClick,
  ariaLabel,
  disabled = false,
  disabledReason,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const label = ariaLabel ?? t('common:table.action.delete')
  const title =
    disabled && disabledReason !== undefined ? disabledReason : label
  return (
    // EditIconButton と同じ理由で DS Tooltip。 disabled 時は理由文言を出す
    // (= 押せない時こそ説明が要る)。 disabled の IconButton は focus を受けないので、
    // ButtonTooltip の包みが Tab の止まり先になる (UI 辞書 components/action-reason.md 区分 2)
    <ButtonTooltip content={title}>
      <IconButton
        color="Text.negativeMid"
        borderColor="Border.negativeMid"
        _hover={{
          backgroundColor: 'Hover.negativeLow',
          _disabled: { backgroundColor: 'Surface.primary' },
        }}
        aria-label={label}
        backgroundColor="Surface.primary"
        colorPalette="red"
        disabled={disabled}
        onClick={(e) => {
          // 通常列 (renderRowActions を使わない自前カラム) から呼ばれる場合に備え、
          // 行クリック (詳細遷移) への伝播をここで止める (= renderRowActions 経由の
          // 呼び出しでは TableCell 側で二重に stopPropagation されるが無害)。
          e.stopPropagation()
          onClick(e)
        }}
        size="sm"
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
        style={{ borderRadius: '4px' }}
        // disabled の要素に native title は付けない (ブラウザは出さない。 action-reason.md 区分 2)。
        // 理由は上の ButtonTooltip が出す
        title={disabled ? undefined : title}
        variant="outline"
      >
        <FaTrashCanRegular boxSize={iconSize.rowAction} />
      </IconButton>
    </ButtonTooltip>
  )
}
